import { replace } from 'lodash-es';
import { useEffect } from 'react';

declare global {
  interface Window {
    AppleID: {
      stravaInit: boolean;
      auth: {
        init: (params: {
          clientId: string;
          scope: string;
          redirectURI: string;
          state: string;
        }) => void;
        signIn: () => void;
      };
    };
  }
}

type LogError = (e: Error | string) => void;

const appendScriptToBody = ({
  script,
  name,
  logError
}: {
  script: Node;
  name: string;
  logError: LogError;
}) => {
  if (document.querySelector('body')) {
    document.querySelector('body')?.appendChild(script);
  } else {
    const e = new Error(`Failed to inject ${name} script`);
    logError(e);
  }
};

const injectAppleScript = ({
  logError,
  locale = 'en_US'
}: {
  logError: LogError;
  locale?: string;
}) => {
  if (!window.AppleID) {
    const script = window.document.createElement('script');
    const formattedLocale = replace(locale, '-', '_');
    script.src = `https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/${formattedLocale}/appleid.auth.js`;
    script.async = true;
    appendScriptToBody({ script, name: 'apple', logError });
  }
};

/**
 *
 * @param {String} state https://developer.apple.com/documentation/sign_in_with_apple/clientconfigi/3230956-state
 */
export const useApple = (state: string, logError: LogError) => {
  useEffect(() => {
    injectAppleScript({ logError });
  }, [logError]);

  const signin = () => {
    if (!window.AppleID.stravaInit) {
      window.AppleID.auth.init({
        clientId: 'com.strava.app.sign.in',
        scope: 'name email',
        redirectURI: 'https://www.strava.com/o_auth/apple',
        state
      });
      window.AppleID.stravaInit = true;
    }
    window.AppleID.auth.signIn();
  };

  return { signin };
};

export default useApple;
